import { ReactNode, useMemo, useState } from 'react';
import { Disclosure } from '@headlessui/react';
import Container from '@v2/shared/components/Container';
import Link from '@v2/shared/components/Link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleUp, faAngleDown, faLongArrowRight, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { OfferCodeSku, Product } from '@v2/shared/lib/types';
import { useSite } from '@v2/shared/lib/site';
import { useCart } from '@v2/shared/lib/cart';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import applyOfferCodeSkus from '@v2/shared/lib/applyOfferCodeSkus';
import { trackAddToCart } from '@v2/shared/lib/analytics';
import PlanCard from './PlanCard';
import bg from '../images/magazine-collage.jpg';

type Props = {
  product: Product;
  offerCodeSkus: OfferCodeSku[];
  offerCode: string | null;
  saleEndsOn: string;
};

/**
 * The plans, and the only thing on the page that puts something in the cart.
 *
 * The product arrives at list prices and the offer's prices arrive beside it;
 * applyOfferCodeSkus joins them here rather than in the props builder, for the reason
 * set out on that file. `offerCode` is what the cart is told to apply, and it is null
 * unless the page proved the code does something.
 */
export default function PricingPanel({ product, offerCodeSkus, offerCode, saleEndsOn }: Props) {
  const { currency } = useSite();
  const { addItem } = useCart();
  const { buildUrl } = useUrlGenerator();
  const [pendingSku, setPendingSku] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  const skus = useMemo(
    () => applyOfferCodeSkus(product.active_skus ?? [], offerCodeSkus),
    [product.active_skus, offerCodeSkus]
  );

  // Renewal skus price a subscription that is already running; they are not on offer to
  // somebody arriving at a landing page.
  const plans = skus.filter((sku) => String(sku.attributes?.renewal ?? '') === '0');
  // The yardstick the annual plans are measured against — "save 66%" means against a
  // year of paying monthly, not against the list price of the plan itself.
  const oneMonthSku = skus.find((sku) => String(sku.attributes?.months ?? '') === '1');

  async function choose(sku: string) {
    setPendingSku(sku);
    setFailed(false);

    try {
      const cart = await addItem(sku, offerCode);
      const added = cart.items?.[cart.items.length - 1];

      // Reported from the line the cart actually holds, so the plan chosen and the price
      // charged are the ones in the event — and its `item_id` is the product's base sku,
      // not this plan's, so all four plans report as one product. Landing on /cart does
      // not report it: the legacy page's `add_to_cart` only covers lines added through
      // its own form.
      if (added) {
        trackAddToCart(added);
      }

      // A full navigation rather than a router push: there is no router here, and the
      // cart is a legacy page that has to render server-side regardless.
      window.location.href = buildUrl('/cart');
    } catch (error) {
      console.error('Could not add the plan to the cart', error);
      setFailed(true);
      // Only on the failure path. On the success path the navigation is already
      // underway, and clearing it would flash the button back to its resting state
      // while the visitor waits for the cart.
      setPendingSku(null);
    }
  }

  return (
    <div
      style={{
        backgroundImage: `url(${bg})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* scroll-mt clears the fixed header, so the browse page's "Choose Your Plan" link
          does not land with this heading behind it — the anchor puts the section's top
          edge at y=0 and the header is painted over it. The header is three heights, and
          the tallest is in the middle rather than at the top: 53px below md, 126px from
          md to xl where the search bar sits on its own row, and 82px at xl once it moves
          up beside the nav. Measured scrolled, which is the state a hash jump lands in. */}
      <Container id="pricing" className="py-8 lg:py-16 scroll-mt-14 md:scroll-mt-32 xl:scroll-mt-24">
        <h2 className="font-bold text-yellow-400 text-3xl text-center px-3 mb-0 lg:mb-6">Choose Your Plan</h2>

        {/* On a narrow screen the four cards are already a long scroll, so the feature
            list collapses out of the way above them. */}
        <div className="flex flex-col lg:hidden items-center mb-3 lg:mb-8">
          <Disclosure>
            {({ open }) => (
              <>
                <Disclosure.Button className="flex items-center text-sm text-white text-opacity-75 cursor-pointer">
                  {open ? 'Hide' : 'Show'}&nbsp;Features{' '}
                  <FontAwesomeIcon icon={open ? faAngleUp : faAngleDown} className="ml-2" />
                </Disclosure.Button>
                <Disclosure.Panel>
                  <div className="mb-4">
                    <Feature>
                      Read 10,000+ magazines and newspapers <br />
                      <Link
                        href="/categories/magzter-gold"
                        className="text-xs text-emerald-400 hover:text-emerald-500 cursor-pointer"
                      >
                        View All <FontAwesomeIcon icon={faLongArrowRight} fixedWidth />
                      </Link>
                    </Feature>
                    <Feature>Access premium stories</Feature>
                    <Feature>Cancel subscription at any time</Feature>
                    <Feature>7-day no questions asked refund guarantee</Feature>
                  </div>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </div>

        {failed && (
          <p className="text-center text-white bg-red-700 rounded-md py-2 px-4 mb-4" role="alert">
            Sorry, we could not add that plan to your cart. Please try again.
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {plans.map((sku) => (
            <PlanCard
              key={sku.id}
              sku={sku}
              oneMonthSku={oneMonthSku}
              currency={currency}
              offerCode={offerCode}
              saleEndsOn={saleEndsOn}
              isAdding={pendingSku === sku.code}
              onChoose={() => choose(sku.code)}
            />
          ))}
        </div>
      </Container>
    </div>
  );
}

function Feature({ children }: { children?: ReactNode }) {
  return (
    <div className="flex flex-row items-start mb-3 last-of-type:mb-0">
      <FontAwesomeIcon icon={faCheck} className="text-emerald-400 mr-2 mt-1" />
      <div className="text-white font-semibold">{children}</div>
    </div>
  );
}
